import { useIsMounted } from 'custom_hooks/useIsMounted';
import { useCallback, useEffect, useState } from 'react';
import { addMissingItems, removeAllItems, take } from 'utils/array';

export type UseBatchQueryTask<TData> = Readonly<{
    key: string;
    query: () => Promise<TData>;
    onSuccess: (result: TData) => void;
    onError?: (error: unknown) => void;
}>;

type QueryState = Readonly<{
    executingCount: number;
    taskList: UseBatchQueryTask<unknown>[];
    queuedKeys: string[];
}>;

/**
 * Hook for loading data on demand.  Executes queries in batches for efficiency.
 *
 * @param batchSize the max number of simultaneous executing queries.
 */
export function useBatchQuery(batchSize: number) {
    const isMountedRef = useIsMounted();

    const [{ executingCount, taskList, queuedKeys }, setQueryState] =
        useState<QueryState>(() => ({
            executingCount: 0,
            taskList: [],
            queuedKeys: [],
        }));

    const enqueue = useCallback(<TData>(tasks: UseBatchQueryTask<TData>[]) => {
        if (tasks.length > 0) {
            const keys = tasks.map((task) => task.key);
            setQueryState((current) => ({
                ...current,
                taskList: [
                    ...current.taskList.filter((task) => !keys.includes(task.key)),
                    ...tasks,
                ] as UseBatchQueryTask<unknown>[],
                queuedKeys: addMissingItems(current.queuedKeys, keys),
            }));
        }
    }, []);

    useEffect(() => {
        const takeCount = Math.min(taskList.length, batchSize - executingCount);

        if (takeCount > 0) {
            const tasks = take(taskList, takeCount);

            setQueryState((current) => ({
                ...current,
                executingCount: current.executingCount + tasks.length,
                taskList: removeAllItems(current.taskList, tasks),
            }));

            const complete = (key: string) =>
                setQueryState((current) => ({
                    ...current,
                    executingCount: current.executingCount - 1,
                    queuedKeys: removeAllItems(current.queuedKeys, [key]),
                }));

            Promise.all(
                tasks.map(async ({ key, query, onSuccess, onError }) => {
                    try {
                        const result = await query();
                        if (isMountedRef.current) {
                            onSuccess(result);
                            complete(key);
                        }
                    } catch (error) {
                        if (isMountedRef.current) {
                            onError?.(error);
                            complete(key);
                        }
                    }
                }),
            );
        }
    }, [batchSize, isMountedRef, executingCount, taskList]);

    return {
        queuedKeys,
        enqueue,
    };
}
